/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import kotlinx.browser.window
import kotlinx.coroutines.*

// External Plaid Link JavaScript API declarations  
external interface PlaidLinkOptions {
    var token: String
    var onSuccess: (publicToken: String, metadata: dynamic) -> Unit
    var onLoad: (() -> Unit)?
    var onExit: ((error: dynamic, metadata: dynamic) -> Unit)?
    var onEvent: ((eventName: String, metadata: dynamic) -> Unit)?
    var receivedRedirectUri: String?
}

external interface PlaidLink {
    fun open()
    fun exit(options: dynamic = definedExternally)
    fun destroy()
}

external object Plaid {
    fun create(options: PlaidLinkOptions): PlaidLink
}

private const val LINK_TOKEN_STORAGE_KEY = "wiz3r_plaid_link_token"

class PlaidLinkHelper(
    private val apiService: ApiService
) {
    private var plaidLink: PlaidLink? = null
    private var daysRequested: Int? = null

    private fun isOAuthRedirect(): Boolean {
        val query = window.location.search ?: return false
        return query.contains("oauth_state_id")
    }

    private fun currentRedirectUri(): String? {
        return if (isOAuthRedirect()) window.location.href else null
    }

    private fun loadStoredLinkToken(): String? {
        return window.localStorage.getItem(LINK_TOKEN_STORAGE_KEY)?.takeIf { it.isNotBlank() }
    }

    private fun storeLinkToken(token: String) {
        window.localStorage.setItem(LINK_TOKEN_STORAGE_KEY, token)
    }

    private fun clearStoredLinkToken() {
        window.localStorage.removeItem(LINK_TOKEN_STORAGE_KEY)
    }

    private fun clearOAuthParametersFromUrl() {
        if (isOAuthRedirect()) {
            val cleanUrl = "${window.location.origin}${window.location.pathname}"
            window.history.replaceState(null, "", cleanUrl)
        }
    }
    
    suspend fun initializePlaidLink(
        daysRequested: Int? = null,
        onSuccess: (accessToken: String) -> Unit,
        onError: (String) -> Unit
    ) {
        this.daysRequested = daysRequested
        try {
            val receivedRedirectUri = currentRedirectUri()

            val linkToken =
                if (receivedRedirectUri != null) {
                    val storedToken = loadStoredLinkToken()
                    if (storedToken != null) {
                        console.log("Resuming Plaid Link with stored token after OAuth redirect")
                        storedToken
                    } else {
                        console.warn("Missing stored Plaid link token for OAuth redirect; requesting a new token.")
                        val freshResponse = apiService.createLinkToken(daysRequested)
                        storeLinkToken(freshResponse.link_token)
                        freshResponse.link_token
                    }
                } else {
                    console.log("Getting link token from server...")
                    val response = apiService.createLinkToken(daysRequested)
                    storeLinkToken(response.link_token)
                    response.link_token
                }
            
            val linkOptions = js("{}").unsafeCast<PlaidLinkOptions>()
            linkOptions.token = linkToken
            linkOptions.receivedRedirectUri = receivedRedirectUri
            linkOptions.onSuccess = { publicToken: String, metadata: dynamic ->
                console.log("Plaid Link success, exchanging token...")
                val instId = metadata?.institution?.institution_id as? String
                val instName = metadata?.institution?.name as? String
                console.log("Institution: $instName ($instId)")
                handleTokenExchange(publicToken, instId, instName, onSuccess, onError)
                cleanup()
            }
            linkOptions.onLoad = {
                console.log("Plaid Link loaded")
            }
            linkOptions.onExit = { error: dynamic, metadata: dynamic ->
                val errorCode = error?.error_code as? String
                val errorType = error?.error_type as? String
                val errorMessage = error?.error_message as? String
                val displayMessage = error?.display_message as? String
                val status = metadata?.status as? String
                val requestId = metadata?.request_id as? String
                val institutionName = metadata?.institution?.name as? String
                console.log(
                    "Plaid Link exit: code=$errorCode, type=$errorType, " +
                        "message=$errorMessage, display=$displayMessage, status=$status, " +
                        "institution=$institutionName, request_id=$requestId"
                )
                cleanup()
                if (error != null) {
                    onError(displayMessage ?: errorMessage ?: errorCode ?: "User cancelled or error occurred")
                }
            }
            linkOptions.onEvent = { eventName: String, metadata: dynamic ->
                val viewName = metadata?.view_name as? String
                val errCode = metadata?.error_code as? String
                console.log("Plaid Link event: $eventName view=$viewName errCode=$errCode")
            }
            
            plaidLink = Plaid.create(linkOptions)
            console.log("Plaid Link initialized, opening...")
            plaidLink?.open()
            
        } catch (e: Exception) {
            console.error("Error initializing Plaid Link:", e)
            onError("Failed to initialize Plaid Link: ${e.message}")
        }
    }
    
    private fun handleTokenExchange(
        publicToken: String,
        institutionId: String?,
        institutionName: String?,
        onSuccess: (String) -> Unit,
        onError: (String) -> Unit
    ) {
        GlobalScope.launch {
            try {
                console.log("Exchanging public token...")
                val exchangeResponse = apiService.exchangePublicToken(publicToken, daysRequested, institutionId, institutionName)
                console.log("Token exchange successful:", exchangeResponse)

                // Store the item_id for reference
                window.localStorage.setItem("plaid_item_id", exchangeResponse.item_id)
                clearStoredLinkToken()
                clearOAuthParametersFromUrl()

                // Pass the item_id to the web app for storage (server handles access token internally)
                onSuccess(exchangeResponse.item_id)

            } catch (e: Exception) {
                console.error("Error exchanging token:", e)
                onError("Failed to exchange token: ${e.message}")
            }
        }
    }
    
    /**
     * Re-authenticate an existing Plaid item without losing data.
     * Plaid Link "update mode" reuses the same item_id; on success we just clear
     * NEEDS_RELINK on the server (no token exchange needed).
     */
    suspend fun initializeRelinkFlow(
        itemId: String,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        try {
            console.log("Requesting relink token for item $itemId")
            val linkToken = apiService.getRelinkToken(itemId)
            // Track success so onExit can decide whether to fire the fallback.
            // Plaid's "Something went wrong" screen exits with error == null, so we can't
            // rely on the error param alone — any exit without success triggers the fallback.
            var didSucceed = false

            val linkOptions = js("{}").unsafeCast<PlaidLinkOptions>()
            linkOptions.token = linkToken
            linkOptions.receivedRedirectUri = currentRedirectUri()
            linkOptions.onSuccess = { _: String, _: dynamic ->
                didSucceed = true
                console.log("Plaid relink success for item $itemId — confirming on server")
                GlobalScope.launch {
                    try {
                        apiService.confirmRelink(itemId)
                        onSuccess()
                    } catch (e: Exception) {
                        console.error("Failed to confirm relink:", e)
                        onError("Reconnected, but failed to refresh data: ${e.message}")
                    }
                }
                cleanup()
            }
            linkOptions.onLoad = { console.log("Plaid Link (update mode) loaded") }
            linkOptions.onExit = { error: dynamic, metadata: dynamic ->
                val errorCode = error?.error_code as? String
                val errorType = error?.error_type as? String
                val errorMessage = error?.error_message as? String
                val displayMessage = error?.display_message as? String
                val status = metadata?.status as? String
                val requestId = metadata?.request_id as? String
                val institutionName = metadata?.institution?.name as? String
                console.log(
                    "Plaid update-mode exit: success=$didSucceed, code=$errorCode, type=$errorType, " +
                        "message=$errorMessage, display=$displayMessage, status=$status, " +
                        "institution=$institutionName, request_id=$requestId"
                )
                cleanup()
                if (!didSucceed) {
                    onError(displayMessage ?: errorMessage ?: errorCode ?: status ?: "exit_without_success")
                }
            }
            linkOptions.onEvent = { eventName: String, eventMetadata: dynamic ->
                val viewName = eventMetadata?.view_name as? String
                val errCode = eventMetadata?.error_code as? String
                console.log("Plaid Link (update mode) event: $eventName view=$viewName errCode=$errCode")
            }

            plaidLink = Plaid.create(linkOptions)
            plaidLink?.open()
        } catch (e: Exception) {
            console.error("Error initializing Plaid relink:", e)
            onError("Failed to start reconnect: ${e.message}")
        }
    }

    fun cleanup() {
        plaidLink?.destroy()
        plaidLink = null
    }
}

// Helper function to check if Plaid Link script is loaded
fun isPlaidLinkLoaded(): Boolean {
    return js("typeof Plaid !== 'undefined'") as Boolean
}

// Helper function to load Plaid Link script dynamically
fun loadPlaidLinkScript(onLoad: () -> Unit) {
    if (isPlaidLinkLoaded()) {
        onLoad()
        return
    }
    
    val script = kotlinx.browser.document.createElement("script")
    script.setAttribute("src", "https://cdn.plaid.com/link/v2/stable/link-initialize.js")
    script.setAttribute("type", "text/javascript")
    script.addEventListener("load", { onLoad() })
    kotlinx.browser.document.head?.appendChild(script)
}
